/**
 * ============================================================
 * Style Playground — Auto-generated metadata
 * ============================================================
 * Device: desktop
 * Language: English
 *
 * ## Style Direction (from analysis)
 * <style-name>ArtisanCatalog</style-name>
 * **Mode**: Light
 * **Style Recipe**: Biophilic / Paper Texture + Editorial / Typography-Driven + Minimalist
 * **Implementation Techniques**: Steps completely away from the standard SaaS look, treating the design system like a high-end brand book or architectural magazine. Uses generous whitespace and an invisible strict grid. Features a very subtle SVG noise/grain overlay on the page background to simulate high-quality paper. Container borders, if used, are hairline thin (0.5px or 1px). Employs high-contrast typography: elegant serif fonts (like Newsreader or Playfair Display) for section headers and component titles, paired with a clean, tracking-adjusted sans-serif for UI details and data.
 * **Color Direction**: Base color is a warm, muted Alabaster or Sand (`#F4F1EB`). Text is rendered in deep Espresso brown or Warm Charcoal instead of black, reducing eye strain and adding elegance. Accents are earthy and organic—Terracotta, Sage Green, and Muted Ochre—used for status tags and interactive highlights, creating a calm, focused, and artisanal environment for designers.
 * ============================================================
 */

import React, { useState } from 'react';
import { Icon } from '@iconify/react';

export default function ArtisanCatalogDashboard() {
  const [activeTab, setActiveTab] = useState('Overview');
  const [hoveredComponent, setHoveredComponent] = useState<string | null>(null);

  const stats = [
    { label: 'Total Components', value: '142', trend: '+12 this month' },
    { label: 'In Draft', value: '12', trend: 'Needs review' },
    { label: 'Under Review', value: '5', trend: '2 urgent' },
    { label: 'Published', value: '125', trend: 'Stable' },
  ];

  const recentUpdates = [
    { id: '1', name: 'Button', version: 'v1.2.0', status: 'Published', time: '2 hours ago', author: 'Emma W.' },
    { id: '2', name: 'Dropdown Menu', version: 'v0.9.4', status: 'Review', time: '5 hours ago', author: 'James L.' },
    { id: '3', name: 'Modal Dialog', version: 'v2.1.1', status: 'Published', time: 'Yesterday', author: 'Sarah K.' },
    { id: '4', name: 'Date Picker', version: 'v1.0.0-beta', status: 'Draft', time: '2 days ago', author: 'Alex M.' },
    { id: '5', name: 'Toast Notification', version: 'v1.1.5', status: 'Published', time: '3 days ago', author: 'Emma W.' },
  ];

  const activities = [
    { id: 'a1', text: 'Emma W. published v1.2.0 of Button', time: '2h ago' },
    { id: 'a2', text: 'Alex M. created draft Date Picker', time: '2d ago' },
    { id: 'a3', text: 'Team review completed for Dropdown', time: '3d ago' },
    { id: 'a4', text: 'Sarah K. updated Design Tokens', time: '4d ago' },
  ];

  return (
    <div className="min-h-screen flex bg-[#F9F8F6] text-[#2C2A29] font-sans overflow-hidden relative selection:bg-[#E8DCC4] selection:text-[#2C2A29]">
      
      {/* Subtle SVG Noise Overlay for Paper Texture */}
      <svg className="pointer-events-none fixed inset-0 opacity-[0.03] z-50 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <filter id="noiseFilter">
          <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch" />
        </filter>
        <rect width="100%" height="100%" filter="url(#noiseFilter)" />
      </svg>

      {/* Left Sidebar - Minimalist & Typography Driven */}
      <aside className="w-64 border-r border-[#E8DCC4] flex flex-col pt-8 pb-6 px-6 shrink-0 relative z-10">
        <div className="flex items-center gap-3 mb-12">
          <div className="w-8 h-8 rounded-full bg-[#2C2A29] flex items-center justify-center text-[#F9F8F6]">
            <Icon icon="lucide:layers" className="w-4 h-4" />
          </div>
          <span className="font-serif text-xl tracking-wide font-medium">ComponentHub</span>
        </div>

        <nav className="flex-1 space-y-8">
          <div>
            <h3 className="text-[10px] uppercase tracking-widest text-[#8A857D] mb-4 font-semibold">Library</h3>
            <ul className="space-y-1">
              {['Dashboard', 'Components', 'Design Tokens', 'Changelog', 'Categories'].map((item) => (
                <li key={item}>
                  <button 
                    className={`w-full text-left py-2 px-3 rounded text-sm transition-colors duration-200 ${item === 'Dashboard' ? 'bg-[#E8DCC4]/50 text-[#2C2A29] font-medium' : 'text-[#5C5855] hover:text-[#2C2A29] hover:bg-[#E8DCC4]/30'}`}
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[10px] uppercase tracking-widest text-[#8A857D] mb-4 font-semibold">Settings</h3>
            <ul className="space-y-1">
              {['Team', 'Integrations', 'Preferences'].map((item) => (
                <li key={item}>
                  <button className="w-full text-left py-2 px-3 rounded text-sm text-[#5C5855] hover:text-[#2C2A29] hover:bg-[#E8DCC4]/30 transition-colors duration-200">
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="mt-auto flex items-center gap-3 pt-6 border-t border-[#E8DCC4]/50">
          <div className="w-8 h-8 rounded-full bg-[#E8DCC4] flex items-center justify-center text-[#5C5855] font-serif italic">
            E
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Emma Woods</span>
            <span className="text-[11px] text-[#8A857D]">Lead Designer</span>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden relative z-10">
        
        {/* Header */}
        <header className="h-20 flex items-center justify-between px-10 shrink-0">
          <h1 className="font-serif text-2xl tracking-wide">Overview</h1>
          
          <div className="flex items-center gap-6">
            <div className="relative group">
              <Icon icon="lucide:search" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A857D] group-hover:text-[#2C2A29] transition-colors" />
              <input 
                type="text" 
                placeholder="Search components, tokens..." 
                className="pl-9 pr-4 py-2 bg-transparent border-b border-[#E8DCC4] focus:border-[#2C2A29] outline-none text-sm placeholder:text-[#8A857D] w-64 transition-colors"
              />
            </div>
            <button className="relative p-2 text-[#5C5855] hover:text-[#2C2A29] transition-colors">
              <Icon icon="lucide:bell" className="w-5 h-5" />
              <span className="absolute top-2 right-2 w-1.5 h-1.5 bg-[#C25E4D] rounded-full"></span>
            </button>
          </div>
        </header>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-auto px-10 pb-12">
          
          {/* Stats Grid - Editorial / Typography Driven */}
          <section className="grid grid-cols-4 gap-8 mb-16 mt-4">
            {stats.map((stat, idx) => (
              <div key={idx} className="flex flex-col border-l border-[#E8DCC4] pl-5 py-1">
                <span className="text-xs text-[#8A857D] uppercase tracking-wider mb-2">{stat.label}</span>
                <span className="font-serif text-4xl mb-1">{stat.value}</span>
                <span className="text-xs text-[#5C5855] italic">{stat.trend}</span>
              </div>
            ))}
          </section>

          <div className="grid grid-cols-12 gap-12">
            
            {/* Recent Updates - Main Column */}
            <section className="col-span-8">
              <div className="flex items-center justify-between mb-8 border-b border-[#E8DCC4] pb-4">
                <h2 className="font-serif text-xl tracking-wide">Recent Catalog Updates</h2>
                <button className="text-xs uppercase tracking-widest text-[#8A857D] hover:text-[#2C2A29] transition-colors flex items-center gap-1">
                  View All <Icon icon="lucide:arrow-right" className="w-3 h-3" />
                </button>
              </div>

              <div className="space-y-1">
                {recentUpdates.map((comp) => (
                  <div 
                    key={comp.id}
                    onMouseEnter={() => setHoveredComponent(comp.id)}
                    onMouseLeave={() => setHoveredComponent(null)}
                    className="group flex items-center justify-between py-4 border-b border-[#E8DCC4]/40 hover:border-[#2C2A29]/20 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-5">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#E8DCC4]/30 text-[#5C5855] group-hover:bg-[#E8DCC4] group-hover:text-[#2C2A29] transition-colors rounded">
                        <Icon icon="lucide:component" className="w-4 h-4" />
                      </div>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-[15px]">{comp.name}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-wider
                            ${comp.status === 'Published' ? 'bg-[#7A8B76]/10 text-[#7A8B76]' : 
                              comp.status === 'Review' ? 'bg-[#D1A054]/10 text-[#D1A054]' : 
                              'bg-[#8A857D]/10 text-[#8A857D]'}`}
                          >
                            {comp.status}
                          </span>
                        </div>
                        <span className="text-xs text-[#8A857D] mt-1 font-mono">{comp.version} · Updated by {comp.author}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-[#8A857D]">
                      <span>{comp.time}</span>
                      <Icon 
                        icon="lucide:arrow-up-right" 
                        className={`w-4 h-4 transition-all duration-300 ${hoveredComponent === comp.id ? 'opacity-100 translate-x-0 text-[#2C2A29]' : 'opacity-0 -translate-x-2'}`} 
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Quick Actions & Activity - Side Column */}
            <section className="col-span-4 flex flex-col gap-10">
              
              {/* Actions */}
              <div>
                <h2 className="font-serif text-lg tracking-wide mb-6">Studio Actions</h2>
                <div className="flex flex-col gap-3">
                  <button className="w-full py-3 px-4 bg-[#2C2A29] text-[#F9F8F6] flex items-center justify-between hover:bg-[#1A1918] transition-colors rounded-sm group">
                    <span className="text-sm font-medium">New Component</span>
                    <Icon icon="lucide:plus" className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
                  </button>
                  <button className="w-full py-3 px-4 bg-transparent border border-[#E8DCC4] text-[#2C2A29] flex items-center justify-between hover:border-[#2C2A29] transition-colors rounded-sm">
                    <span className="text-sm font-medium">Manage Design Tokens</span>
                    <Icon icon="lucide:settings-2" className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Activity Log */}
              <div>
                <h2 className="font-serif text-lg tracking-wide mb-6">Studio Ledger</h2>
                <div className="relative before:absolute before:left-[5px] before:top-2 before:bottom-2 before:w-[1px] before:bg-[#E8DCC4]">
                  <ul className="space-y-5">
                    {activities.map((activity) => (
                      <li key={activity.id} className="relative pl-6">
                        <span className="absolute left-0 top-1.5 w-[11px] h-[11px] rounded-full bg-[#F9F8F6] border-2 border-[#D1A054]"></span>
                        <p className="text-sm text-[#2C2A29] leading-snug">{activity.text}</p>
                        <span className="text-xs text-[#8A857D] mt-1 block italic">{activity.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
